import * as THREE from 'three';

/**
 * Runway — "how far can the player actually walk from here, facing there".
 *
 * A chest-height raycast is not enough: it happily reports 40 m of clear
 * space over a hole in the floor, or misses the corner of a crate the
 * player's shoulder would clip. This marches the standing capsule forward,
 * checking both that there is ground and that the capsule fits.
 *
 * Used by the shot setups and the measurement suite so both pick sane,
 * level-agnostic test directions.
 */

const _p = new THREE.Vector3();
const _f = new THREE.Vector3();
const DOWN = new THREE.Vector3(0, -1, 0);

export function forwardFromYaw(yaw, out = new THREE.Vector3()) {
  return out.set(-Math.sin(yaw), 0, -Math.cos(yaw));
}

export function rightFromYaw(yaw, out = new THREE.Vector3()) {
  return out.set(Math.cos(yaw), 0, -Math.sin(yaw));
}

/**
 * @returns {number} metres of walkable floor from `feet` along `yaw`.
 */
export function runwayLength(adapter, cfg, feet, yaw, max = 26, stride = 0.35, flatTol = null) {
  const tol = flatTol ?? cfg.stepHeight + 0.05;
  forwardFromYaw(yaw, _f);
  let lastY = feet.y;
  for (let d = stride; d <= max; d += stride) {
    const x = feet.x + _f.x * d, z = feet.z + _f.z * d;
    _p.set(x, lastY + 1.2, z);
    const h = adapter.raycast(_p, DOWN, 3.2);
    if (!h || h.normal.y < 0.55) return d - stride;
    const y = h.point.y;
    if (Math.abs(y - lastY) > tol) return d - stride;
    _p.set(x, y, z);
    if (!adapter.capsuleFits(_p, cfg.standHeight, cfg.radius + 0.06)) return d - stride;
    lastY = y;
  }
  return max;
}

/**
 * Best straight-line runway from `feet`, scanning `steps` headings.
 * Deterministic: same level, same answer.
 * @returns {{yaw:number, dist:number}}
 */
export function bestRunway(adapter, cfg, feet, steps = 48, max = 26, preferYaw = null, flatTol = null) {
  let bestYaw = preferYaw ?? 0;
  let bestDist = -1;
  for (let i = 0; i < steps; i++) {
    const yaw = (i / steps) * Math.PI * 2;
    const d = runwayLength(adapter, cfg, feet, yaw, max, 0.35, flatTol);
    /* tiny bias toward the level's own spawn facing, so shots stay stable
       when two directions are equally open */
    const score = d + (preferYaw !== null ? Math.cos(yaw - preferYaw) * 0.25 : 0);
    if (score > bestDist) { bestDist = score; bestYaw = yaw; }
  }
  return { yaw: bestYaw, dist: runwayLength(adapter, cfg, feet, bestYaw, max, 0.35, flatTol) };
}

/** Best axis-aligned runway (test geometry is axis-aligned). */
export function bestAxisRunway(adapter, cfg, feet, need = 10, max = 26) {
  let best = null;
  for (const yaw of [0, Math.PI * 0.5, Math.PI, Math.PI * 1.5]) {
    const dist = runwayLength(adapter, cfg, feet, yaw, max);
    if (!best || dist > best.dist) best = { yaw, dist };
  }
  return best && best.dist >= need ? best : null;
}

export default bestRunway;
